import SaveIcon from '@mui/icons-material/Save';
import type { Theme } from '@mui/material';
import { Button, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useUnitProvider } from '/@/core/context/unit';
import { useUpdateHumidityAtHarvestCropMonitoringResult } from '/@/hooks/reports';
import { _ } from '/@/locale';
import type { Brand } from '/@/types/catalog';
import type { Units } from '/@/types/core';
import type { CropMonitoring } from '/@/types/crop-monitoring';

const ReportMetrics = ({
    cropMonitoring,
    unitsProp,
    isPdf,
}: {
    cropMonitoring: CropMonitoring;
    unitsProp?: { units: Units; language: string; brands: Brand[] };
    isPdf: boolean;
}) => {
    const units = useUnitProvider(unitsProp);
    const [isEditing, setIsEditing] = useState(false);
    const [humidityAtHarvest, setHumidityAtHarvest] = useState<number | undefined>(undefined);
    const updateHumidityAtHarvestCropMonitoringResult = useUpdateHumidityAtHarvestCropMonitoringResult({
        cropMonitoringId: cropMonitoring?.id,
        fieldId: cropMonitoring?.field,
    });
    const { enqueueSnackbar } = useSnackbar();

    const report = cropMonitoring?.result;

    const handleSaveHumidityAtHarvest = () => {
        try {
            updateHumidityAtHarvestCropMonitoringResult.trigger({
                humidityAtHarvest: humidityAtHarvest,
            });
            setIsEditing(false);
            enqueueSnackbar(_('Guardamos los cambios. Nos ayuda a mejorar futuras predicciones.'), {
                variant: 'success',
                autoHideDuration: 3000,
            });
        } catch (error) {
            enqueueSnackbar(error.message, {
                variant: 'error',
                autoHideDuration: 3000,
            });
        }
    };

    const reportData: {
        title: string | React.ReactNode;
        description: string | React.ReactNode;
        caption?: React.ReactNode;
    }[] = [
        {
            title: _('Rendimiento'),
            description: (
                <Typography variant='h4' fontWeight='semibold' color='primary'>
                    {(units.toWeight(report?.averageYield || 0, 2) || 0).value} {units.getUnit('weight/area')}
                </Typography>
            ),
        },
        ...(report?.totalHarvestedArea
            ? [
                  {
                      title: _('Hectáreas cosechadas'),
                      description: (
                          <Typography variant='h4' fontWeight='semibold' color={'primary'}>
                              {units.toArea(report?.totalHarvestedArea, 2).toString()}
                          </Typography>
                      ),
                  },
              ]
            : []),
        {
            title: (
                <Stack direction='row' alignItems='center' spacing={1}>
                    <Typography variant='h6' color='primary' fontWeight='semibold'>
                        {_('Humedad al cosechar')}
                    </Typography>
                </Stack>
            ),
            description: isEditing ? (
                <TextField
                    value={humidityAtHarvest}
                    onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || Number(value) >= 0) {
                            setHumidityAtHarvest(Number(value));
                        }
                    }}
                    type='number'
                    inputProps={{ min: 0 }}
                    fullWidth
                    InputProps={{
                        endAdornment: (
                            <>
                                <InputAdornment position='end'>%</InputAdornment>
                                <InputAdornment
                                    position='end'
                                    sx={{ cursor: 'pointer' }}
                                    onClick={handleSaveHumidityAtHarvest}
                                >
                                    <SaveIcon sx={{ fontSize: 20, color: 'primary.mui.inactive' }} />
                                </InputAdornment>
                            </>
                        ),
                    }}
                />
            ) : (
                <Typography variant='h6' color='primary' fontWeight='semibold'>
                    {report?.humidityAtHarvest?.toFixed(2)}%
                </Typography>
            ),
            caption: !isEditing &&
                !isPdf &&
                (!report?.harvestReport || !report?.plantingReport) && ( //only show if it's not a fieldview report
                    <Button
                        variant='text'
                        size='small'
                        color='secondary'
                        sx={{ p: 0, textDecoration: 'underline' }}
                        onClick={() => {
                            setHumidityAtHarvest(report?.humidityAtHarvest);
                            setIsEditing(true);
                        }}
                    >
                        {_('Editar humedad')}
                    </Button>
                ),
        },
    ];
    return (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent='space-between' alignItems='stretch'>
            {reportData.map((item, key) => (
                <Stack
                    width='100%'
                    flexGrow={1}
                    direction='column'
                    key={`${item.title}-${key}-${item.description}`}
                    spacing={1}
                    sx={{
                        p: 2,
                        border: (t: Theme) => `1px solid ${t.palette.divider}`,
                    }}
                >
                    <Typography color='primary' fontWeight='semibold' variant='h6'>
                        {item.title}
                    </Typography>
                    <Typography color='primary.mui.inactive'>{item.description}</Typography>
                    <Typography color='primary.mui.inactive'>{item.caption}</Typography>
                </Stack>
            ))}
        </Stack>
    );
};

export default ReportMetrics;
